import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { useApiStreamPostRequest } from '@/api/rest/hooks/useApiStreamPostRequest';
import {
  Action,
  Message,
  MessageType,
  POST_ORGANIZATION_CONVERSATIONS_PATH,
} from '@/api/rest/resources/types/conversation';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { isNullish } from '@/utils/booleans';

import { DEFAULT_GREETING_MESSAGE } from '../constants';

type ConversationProps = {
  sendMessage: (message: string, conversationIdArg?: string, agentTypeArg?: string) => Promise<void>;
  executeActions: (actions: Action[], conversationIdArg?: string) => Promise<void>;
  isStreamInProgress: boolean;
  bufferedMessages: Partial<Message>[];
  thinkingMessages: string[];
  streamingContent: string;
  actions: Action[];
  error: string | null;
  conversationId: string | null;
  agentType: string | null;
  pendingUserMessage: string | null;
};

export const useUpdateConversation = (
  agentTypeArg: string | null,
  conversationIdArg: string | null,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): ConversationProps => {
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  const [conversationId, setConversationId] = useState<string | null>(conversationIdArg);
  const [agentType, setAgentType] = useState<string | null>(agentTypeArg);

  const [isStreamInProgress, setIsStreamInProgress] = useState(false);
  const [bufferedMessages, setBufferedMessages] = useState<Partial<Message>[]>(
    conversationIdArg ? [] : [DEFAULT_GREETING_MESSAGE],
  );

  const [thinkingMessages, setThinkingMessages] = useState<string[]>([]);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [actions, setActions] = useState<Action[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);

  const resetMessageState = useCallback(() => {
    setThinkingMessages([]);
    setStreamingContent('');
    setActions([]);
    setError(null);
  }, []);

  const streamConversation = useApiStreamPostRequest<Message, Partial<Message>>(POST_ORGANIZATION_CONVERSATIONS_PATH, {
    onChunk: (message: Message) => {
      try {
        const eventType = message.context?.type || MessageType.BUFFERED;
        switch (eventType as MessageType) {
          case MessageType.STREAM: {
            const contentType = message.context?.content_type;
            if (contentType === 'thinking') {
              const thinkingContent = message.content || '';
              setThinkingMessages((prev) => [...prev, thinkingContent]);
            } else if (contentType === 'content') {
              const contentChunk = message.content || '';
              setStreamingContent((prev) => prev + contentChunk);
              if (message.context?.actions) {
                const messageActions = message.context.actions as Action[];
                setActions(messageActions);
              }
            }
            break;
          }
          case MessageType.BUFFERED: {
            if (message.is_user) {
              setPendingUserMessage(null);
            }
            if (message.content) {
              setBufferedMessages((prev) => [...prev, message]);
            }
            if (message.context?.actions) {
              const messageActions = message.context.actions as Action[];
              setActions(messageActions);
            }

            if (message.context?.invalidate_queries) {
              message.context.invalidate_queries.forEach((queryKey: string[]) => {
                queryClient.invalidateQueries({ queryKey });
              });
            }
            break;
          }
          case MessageType.SUCCESS: {
            setIsStreamInProgress(false);

            if (message.context?.conversation_id) {
              setConversationId(message.context.conversation_id);
            }
            if (message.context?.agent_type) {
              setAgentType(message.context.agent_type);
            }
            break;
          }
          case MessageType.ERROR: {
            setError(message.content || 'Unknown error occurred');
            setIsStreamInProgress(false);
            setBufferedMessages((prev) => [...prev, message]);
            break;
          }
          default: {
            setError(`Unknown message type received: ${eventType}`);
            break;
          }
        }
      } catch (e) {
        setError(`Failed to read response: ${e}`);
        setIsStreamInProgress(false);
      }
    },
  });

  const sendMessage = useCallback(
    async (message: string, convId?: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      setIsStreamInProgress(true);
      setPendingUserMessage(message);
      await streamConversation.mutateAsync({
        pathVariables: { organization_id: organizationId },
        queryParams: {
          agent_type: agentType ?? '',
          conversation_id: conversationId ?? convId ?? '',
        },
        body: {
          content: message,
          conversation_id: convId,
          is_user: true,
        },
        signal: abortControllerRef.current.signal,
      });
      resetMessageState();
    },
    [streamConversation, organizationId, agentType, conversationId, resetMessageState],
  );

  const executeActions = useCallback(
    async (selectedActions: Action[], convId?: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      setIsStreamInProgress(true);

      const randomFirstAction = selectedActions.at(0);
      if (isNullish(randomFirstAction)) {
        setError('No actions provided to execute');
        setIsStreamInProgress(false);
        return;
      }

      const actionWithContext =
        selectedActions.length > 1
          ? {
              ...randomFirstAction,
              context: {
                ...randomFirstAction.context,
                selected_values: selectedActions.map((action) => action.context?.selected_values || action.id),
              },
            }
          : randomFirstAction;
      const multipleActionsContent =
        selectedActions.length > 1 ? selectedActions.map((action) => action.name).join(', ') : randomFirstAction.name;

      setPendingUserMessage(multipleActionsContent);
      await streamConversation.mutateAsync({
        pathVariables: { organization_id: organizationId },
        queryParams: {
          agent_type: agentType ?? '',
          conversation_id: conversationId ?? convId ?? '',
        },
        body: {
          content: multipleActionsContent,
          context: { actions: [actionWithContext] },
          conversation_id: convId,
          is_user: true,
        },
        signal: abortControllerRef.current.signal,
      });
      resetMessageState();
    },
    [streamConversation, organizationId, agentType, conversationId, resetMessageState],
  );

  return {
    sendMessage,
    executeActions,
    isStreamInProgress,
    bufferedMessages,
    thinkingMessages,
    streamingContent,
    actions,
    error,
    conversationId,
    agentType,
    pendingUserMessage,
  };
};
